import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import { useAdminAuth } from '../AuthProvider'
import { useAdminFixturesFromQuery } from '../fixtures'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { safeAdminError } from '../safe-ui'

type BridgeState = {
  configured: boolean
  reachable: boolean
  mt5Ready: boolean
  status: string
  message: string
}

type ControlState = {
  pending?: boolean
  reconnectRequestedAt?: string | null
  reconnectAckedAt?: string | null
  lastWatchdogAt?: string | null
  lastWatchdogResult?: string | null
}

export function AdminSettingsPage() {
  const fixtures = useAdminFixturesFromQuery()
  const { logout } = useAdminAuth()
  const navigate = useNavigate()
  const [environment, setEnvironment] = useState<string | null>(null)
  const [uniwireState, setUniwireState] = useState<string>('Managed server-side')
  const [error, setError] = useState('')
  const [bridge, setBridge] = useState<BridgeState | null>(null)
  const [control, setControl] = useState<ControlState | null>(null)
  const [bridgeNote, setBridgeNote] = useState('')
  const [bridgeBusy, setBridgeBusy] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwBusy, setPwBusy] = useState(false)
  const [pwNote, setPwNote] = useState('')

  const refreshBridge = useCallback(async () => {
    if (fixtures) {
      setBridge({
        configured: true,
        reachable: true,
        mt5Ready: true,
        status: 'up',
        message: 'Bridge up · fixture',
      })
      setControl({ pending: false })
      return
    }
    const res = await adminApi.bridgeStatus()
    setBridge(res.bridge)
    setControl(res.control)
  }, [fixtures])

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (fixtures) {
        setEnvironment('fixture')
        setUniwireState('Available (fixture)')
        setBridge({
          configured: true,
          reachable: true,
          mt5Ready: true,
          status: 'up',
          message: 'Bridge up · fixture',
        })
        return
      }
      try {
        const health = await adminApi.health().catch(() => null)
        if (!cancelled && health?.environment) {
          setEnvironment(String(health.environment))
        } else if (!cancelled) {
          setEnvironment('Unavailable')
        }

        const uni = await adminApi
          .listDeposits({ provider: 'UNIWIRE', page: 1, pageSize: 1 })
          .catch(() => null)
        if (!cancelled) {
          if (uni) {
            const total = uni.pagination?.total ?? uni.deposits.length
            setUniwireState(total > 0 ? 'Available (provider deposits observed)' : 'Managed server-side')
          } else {
            setUniwireState('Managed server-side')
          }
        }

        await refreshBridge()
      } catch (err) {
        if (!cancelled) setError(safeAdminError(err, 'Unable to load operational status'))
      }
    }
    void load()
    const id = window.setInterval(() => {
      void refreshBridge().catch(() => undefined)
    }, 15000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [fixtures, refreshBridge])

  async function onReconnect() {
    if (fixtures) {
      setBridgeNote('Fixture mode — reconnect is simulated only.')
      return
    }
    setBridgeBusy(true)
    setBridgeNote('')
    setError('')
    try {
      const res = await adminApi.bridgeReconnect()
      setBridge(res.bridge)
      setControl(res.control)
      setBridgeNote(
        res.message ||
          'Reconnect signal sent. Windows watchdog will restart bridge + tunnel shortly.',
      )
      window.setTimeout(() => {
        void refreshBridge().catch(() => undefined)
      }, 8000)
    } catch (err) {
      setError(safeAdminError(err, 'Unable to request bridge reconnect'))
    } finally {
      setBridgeBusy(false)
    }
  }

  const q = fixtures ? '?fixtures=1' : ''
  const bridgeTone = bridge?.status === 'up' ? 'ok' : 'warn'

  async function onChangePassword(e: FormEvent) {
    e.preventDefault()
    setPwBusy(true)
    setError('')
    setPwNote('')
    try {
      if (newPassword.length < 8) throw new Error('New password must be at least 8 characters')
      if (newPassword !== confirmPassword) throw new Error('New password confirmation does not match')
      await adminApi.changePassword(currentPassword, newPassword)
      setPwNote('Password updated. Sign in again with the new password.')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      window.setTimeout(() => {
        logout()
        navigate('/admin/login', { replace: true })
      }, 900)
    } catch (err) {
      setError(safeAdminError(err, 'Unable to change password'))
    } finally {
      setPwBusy(false)
    }
  }

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Settings" title="Operational status">
        <p className="cp-muted">Bridge reconnect is available here. Secrets stay server-side.</p>
      </PageHeader>

      {error ? <p className="cp-error">{error}</p> : null}
      {pwNote ? <p className="cp-success">{pwNote}</p> : null}

      <div className="cp-panel">
        <h2>Change password</h2>
        <form className="cp-form" onSubmit={(e) => void onChangePassword(e)}>
          <label className="cp-field">
            Current password
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </label>
          <label className="cp-field">
            New password
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              required
            />
          </label>
          <label className="cp-field">
            Confirm new password
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={8}
              required
            />
          </label>
          <button type="submit" className="pp-btn pp-btn--primary" disabled={pwBusy || Boolean(fixtures)}>
            {pwBusy ? 'Updating…' : 'Update password'}
          </button>
          {fixtures ? (
            <p className="cp-muted" style={{ marginTop: '0.5rem' }}>
              Unavailable in fixture mode.
            </p>
          ) : null}
        </form>
      </div>

      <div className="cp-panel">
        <h2>MT5 Bridge</h2>
        <dl className="cp-stat-grid">
          <div>
            <dt>Status</dt>
            <dd>
              <span className={`cp-chip cp-chip--${bridgeTone}`}>
                {(bridge?.status || 'unknown').toUpperCase()}
              </span>
            </dd>
          </div>
          <div>
            <dt>Detail</dt>
            <dd>{bridge?.message || '—'}</dd>
          </div>
          <div>
            <dt>Reconnect pending</dt>
            <dd>{control?.pending ? 'Yes — waiting for Windows watchdog' : 'No'}</dd>
          </div>
          <div>
            <dt>Last watchdog</dt>
            <dd>
              {control?.lastWatchdogAt || '—'}
              {control?.lastWatchdogResult ? ` · ${control.lastWatchdogResult}` : ''}
            </dd>
          </div>
        </dl>

        <p className="cp-actions-row">
          <button
            type="button"
            className="pp-btn pp-btn--primary"
            disabled={bridgeBusy || !bridge?.configured}
            onClick={() => void onReconnect()}
          >
            {bridgeBusy ? 'Sending…' : 'Bring bridge back up'}
          </button>
          <button
            type="button"
            className="pp-btn pp-btn--secondary"
            disabled={bridgeBusy}
            onClick={() =>
              void refreshBridge().catch((err) =>
                setError(safeAdminError(err, 'Unable to refresh bridge status')),
              )
            }
          >
            Refresh status
          </button>
        </p>

        {bridgeNote ? <p className="cp-note">{bridgeNote}</p> : null}
        <p className="cp-note">
          Windows host must keep <code>bridge\scripts\bridge_watchdog.ps1</code> running. The button
          sends a reconnect signal; the watchdog restarts the bridge process and SSH tunnel.
        </p>
      </div>

      <div className="cp-panel">
        <h2>Environment</h2>
        <dl className="cp-stat-grid">
          <div>
            <dt>Application environment</dt>
            <dd>{environment || '—'}</dd>
          </div>
          <div>
            <dt>MT5 environment</dt>
            <dd>Demo</dd>
          </div>
          <div>
            <dt>Support email</dt>
            <dd>support@perfectpredict.com</dd>
          </div>
        </dl>
      </div>

      <div className="cp-panel">
        <h2>Integrations</h2>
        <dl className="cp-stat-grid">
          <div>
            <dt>Demo account creation</dt>
            <dd>Managed server-side</dd>
          </div>
          <div>
            <dt>Balance operations</dt>
            <dd>Managed server-side</dd>
          </div>
          <div>
            <dt>Uniwire</dt>
            <dd>{uniwireState}</dd>
          </div>
          <div>
            <dt>Uniwire callback path</dt>
            <dd>https://api.perfectpredict.com/api/webhooks/uniwire</dd>
          </div>
        </dl>
        <p className="cp-note">API keys, passwords, and secrets are never displayed.</p>
      </div>

      <StateBlock tone="info" title="Not available in UI">
        Leverage Change, Group Change, Force Credit, Balance Adjustment, and Manual Uniwire Approval are intentionally
        absent.
      </StateBlock>

      <Link className="pp-btn pp-btn--secondary" to={`/admin${q}`}>
        Back to dashboard
      </Link>
    </section>
  )
}
